import React, { useState } from 'react';
import { FaCheck, FaClock, FaStar } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import ApiService from '../../services/apiService';

const MembershipCard = ({ plan, onPaymentSuccess }) => {
    const { currentUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handlePurchase = async () => {
        if (!currentUser) {
            setError('Please log in to purchase a membership');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Get Stripe prices from backend
            const prices = await ApiService.getPlanPrices();
            const planPrice = prices.find(p => p.plan_id === plan.id);
            
            if (!planPrice) {
                throw new Error('Pricing not available for this plan');
            }

            // Create checkout session
            const successUrl = `${window.location.origin}/payment-success`;
            const cancelUrl = `${window.location.origin}/membership?payment=cancelled`;
            
            const { url } = await ApiService.createCheckoutSession(
                planPrice.id,
                successUrl,
                cancelUrl,
                currentUser.uid // Pass user ID for webhook processing
            );

            // Redirect to Stripe checkout
            window.location.href = url;
        } catch (err) {
            console.error('Payment error:', err);
            setError(err.message || 'Failed to initiate payment');
        } finally {
            setLoading(false);
        }
    };

    const getColorClasses = (color) => {
        const colorMap = {
            blue: 'bg-blue-500 text-white border-blue-500',
            green: 'bg-green-500 text-white border-green-500',
            purple: 'bg-purple-500 text-white border-purple-500'
        };
        return colorMap[color] || 'bg-primary text-white border-primary';
    };

    const getHoverClasses = (color) => {
        const hoverMap = {
            blue: 'hover:bg-blue-600',
            green: 'hover:bg-green-600',
            purple: 'hover:bg-purple-600'
        };
        return hoverMap[color] || 'hover:bg-primary-dark';
    };

    return (
        <div className={`relative rounded-2xl p-8 shadow-xl transition-all duration-300 hover:shadow-2xl ${
            plan.isPopular 
                ? 'bg-gradient-to-br from-primary to-primary-dark text-white border-2 border-primary/20 scale-105' 
                : 'bg-white text-gray-900 border border-gray-200'
        }`}>
            {plan.isPopular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-yellow-400 text-yellow-900 px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                        <FaStar className="w-3 h-3" />
                        Most Popular
                    </div>
                </div>
            )}

            <div className="text-center mb-8">
                <div className="flex items-center justify-center mb-4">
                    <FaClock className="w-6 h-6 mr-2" />
                    <h3 className="text-2xl font-bold">{plan.name}</h3>
                </div>
                
                <div className="mb-4">
                    <div className="text-4xl font-bold mb-1">{plan.price}</div>
                    <div className="text-sm opacity-80">{plan.duration}</div>
                    {plan.originalPrice && (
                        <div className="text-sm line-through opacity-60 mt-1">
                            {plan.originalPrice}
                        </div>
                    )}
                </div>
                
                <p className="text-sm opacity-80">{plan.description}</p>
            </div>

            <ul className="space-y-4 mb-8">
                {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-3">
                        <FaCheck className={`w-5 h-5 mt-1 flex-shrink-0 ${
                            plan.isPopular ? 'text-green-300' : 'text-green-500'
                        }`} />
                        <span className="text-sm">{feature}</span>
                    </li>
                ))}
            </ul>

            {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-lg">
                    <p className="text-red-700 text-sm">{error}</p>
                </div>
            )}

            <div className="text-center">
                <button 
                    onClick={handlePurchase}
                    disabled={loading}
                    className={`w-full py-3 px-6 rounded-full font-medium transition-all duration-200 ${
                        plan.isPopular 
                            ? 'bg-white text-primary hover:bg-gray-100' 
                            : `${getColorClasses(plan.color)} ${getHoverClasses(plan.color)}`
                    } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    {loading ? (
                        <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                            Processing...
                        </div>
                    ) : (
                        `Get ${plan.name} - ${plan.price}`
                    )}
                </button>
            </div>
        </div>
    );
};

export default MembershipCard;
